import { createContext, useContext } from 'react';

// Tree-wide info shared from DynamicTree → all CustomLabel instances via
// React context, so we don't have to subscribe to useGetApiLibraryTreeQuery
// inside every tree row (N subscriptions vs 1, which caused noticeable
// initial-load jank on larger trees).
//
// Add additional tree-derived data here when more per-row consumers need it
// (e.g. ancestor lookups, breadcrumbs, sibling counts at depth, etc.). Keep
// values referentially stable across renders so memoised consumers don't
// thrash.

export type LibraryTreeContextValue = {
  // IDs of items at the root of the tree (top-level programs). Used by
  // CustomLabel to decide whether to show the drag handle on a sole root
  // item (where there's nothing to reorder against).
  rootItemIds: Set<string>;
  // entityType of every node keyed by its tree id. Lets consumers resolve a
  // node's parent type synchronously (e.g. ActionMenu's Delete-File gate,
  // which must be correct on the menu's first render).
  entityTypesById: Map<string, string>;
};

const defaultValue: LibraryTreeContextValue = {
  rootItemIds: new Set<string>(),
  entityTypesById: new Map<string, string>(),
};

const LibraryTreeContext =
  createContext<LibraryTreeContextValue>(defaultValue);

export const LibraryTreeProvider = LibraryTreeContext.Provider;

export const useLibraryTreeContext = () => useContext(LibraryTreeContext);
